'use client';

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Search } from 'lucide-react';
import Link from 'next/link';
import BlogCard from '@/components/blog/BlogCard';
import { BlogList } from '@/types';

const STATIC_TEXT = {
  tag: 'Knowledge Center',
  headingPart1: 'Our',
  headingPart2: 'Blog',
  subtitle: 'Explore our latest articles and insights on document services, government processes, and expert tips.',
  searchPlaceholder: 'Search articles...',
  allCategoryLabel: 'All',
  noArticlesTitle: 'No articles found',
  noArticlesSubtitle: 'Try adjusting your search or filter criteria'
};

const MemoizedBlogCard = React.memo(BlogCard);

type Props = { initialBlogs: BlogList[] };

function BlogsPageClient({ initialBlogs }: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredBlogs, setFilteredBlogs] = useState<BlogList[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [groupedCategories, setGroupedCategories] = useState<string[]>([]);

  const blogs = initialBlogs;
  const loading = false;

  const memoizedGroupedCategories = useMemo(() => {
    if (!blogs || blogs.length === 0) return [];

    const categorySet = new Set<string>();
    blogs.forEach(blog => {
      if (blog.category) {
        categorySet.add(blog.category);
      }
    });

    return Array.from(categorySet);
  }, [blogs]);

  useEffect(() => {
    setGroupedCategories(memoizedGroupedCategories);
  }, [memoizedGroupedCategories]);

  const calculateFilteredBlogs = useMemo(() => {
    if (!blogs || blogs.length === 0) return [];

    let filtered = blogs;

    if (searchQuery) {
      filtered = filtered.filter(blog =>
        blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        blog.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        blog.category?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (activeCategory) {
      filtered = filtered.filter(blog => blog.category === activeCategory);
    }

    return filtered;
  }, [blogs, searchQuery, activeCategory]);

  useEffect(() => {
    setFilteredBlogs(calculateFilteredBlogs);
  }, [calculateFilteredBlogs]);

  useEffect(() => {
    if (!blogs || blogs.length === 0) return;
    setFilteredBlogs(blogs);
  }, [blogs]);

  const handleCategoryClick = useCallback((category: string) => {
    setActiveCategory(prevCategory => prevCategory === category ? '' : category);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const blogsList = useMemo(() => {
    return filteredBlogs.length > 0 ? (
      <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredBlogs.map((blog) => (
          <MemoizedBlogCard key={blog.id} blog={blog} />
        ))}
      </ul>
    ) : (
      <div className="text-center py-12">
        <h3 className="text-xl font-semibold mb-2">{STATIC_TEXT.noArticlesTitle}</h3>
        <p className="text-gray-500">{STATIC_TEXT.noArticlesSubtitle}</p>
      </div>
    );
  }, [filteredBlogs]);

  const categoryButtons = useMemo(() => {
    return (
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => handleCategoryClick('')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            activeCategory === ''
              ? 'bg-primary text-primary-foreground'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {STATIC_TEXT.allCategoryLabel}
        </button>
        {groupedCategories.map((category) => (
          <button
            key={category}
            onClick={() => handleCategoryClick(category)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeCategory === category
                ? 'bg-primary text-primary-foreground'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {category}
          </button>
        ))}
      </div>
    );
  }, [groupedCategories, activeCategory, handleCategoryClick]);

  return (
    <div className="relative min-h-[90vh]">
      <div className="absolute inset-0 overflow-hidden bg-gradient-to-b from-white to-gray-50">
        <div
          className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-primary/10 opacity-70 blur-3xl"
          aria-hidden="true"
        ></div>
        <div
          className="absolute left-1/4 top-1/3 w-72 h-72 rounded-full bg-gray-200 opacity-60 blur-3xl"
          aria-hidden="true"
        ></div>
        <div
          className="absolute -left-12 bottom-0 w-80 h-80 rounded-full bg-gray-300 opacity-50 blur-3xl"
          aria-hidden="true"
        ></div>
      </div>

      <div className="relative z-10">
        <div className="w-full bg-gray-50 web-only">
          <div className="container py-2">
            <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Link href="/" className="hover:text-foreground">Home</Link>
              <span>/</span>
              <span className="text-foreground">Blog</span>
            </nav>
          </div>
        </div>

        <div className="container py-12 pt-14">
          <div className="text-left mb-12 animate-fade-in">
            <span className="inline-block px-4 py-1.5 mb-6 rounded-full bg-gray-100 text-gray-700 font-medium text-sm">
              {STATIC_TEXT.tag}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-primary">{STATIC_TEXT.headingPart1}</span>{' '}
              <span className="text-gray-900">{STATIC_TEXT.headingPart2}</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              {STATIC_TEXT.subtitle}
            </p>
          </div>

          <div className="mb-10">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="text"
                  placeholder={STATIC_TEXT.searchPlaceholder}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
            </div>

            {categoryButtons}
          </div>

          <div className="min-h-[300px]">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-pulse text-gray-400">Loading articles...</div>
              </div>
            ) : (
              blogsList
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(BlogsPageClient);
